import { useEffect, useRef, useState } from "react";
import {
  AppBar,
  ClickAwayListener,
  Container,
  Grid,
  Grow,
  MenuList,
  Paper,
  Popper,
  Toolbar,
  Box,
  IconButton,
  InputBase,
  Fade,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useParams, useLocation } from "react-router";

import { ReactComponent as ArrowDown } from "assets/icons/chevron-down.svg";
import Logo from "assets/images/logo-prefeitura.png";
import {
  AppBarWrapper,
  LogoImage,
  MenuButonGridItem,
  MenuButton,
  MenuListOption,
  MenuListWrapper,
  SubMenuItem,
  EntityInfoWrapper,
  EntityTypeText,
  EntityCityName,
  LogoEntityWrapper,
} from "./navBar.styles";
import { ReactComponent as Info } from "assets/icons/info.svg";
import { ReactComponent as Menu } from "assets/icons/menu.svg";
import { FlyoutMenuItem } from "./flyoutMenuItem";
import DrawerComponent from "../drawer";
import { useGlobalState } from "../../../global/context/global-context";
import { formatEntityName } from "../../../../utils/entity-formatter";
import { MegaMenuComponent } from "./megaMenu";
import { useMenusData } from "../../../hooks/useMenusData";
import { useMenuLinkNavigation } from "../../../hooks/useMenuLinkNavigation";
import { buildNavStructure, entryPaths, FALE_CONOSCO, NavEntry } from "./navStructure";

interface NavDropdownProps {
  entry: NavEntry;
  active: boolean;
  onNavigate: (path: string, isExternal?: boolean) => void;
}

// Menu da barra principal com dropdown de itens (e submenus em flyout, como Gestão Fiscal)
function NavDropdown({ entry, active, onNavigate }: NavDropdownProps) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const listId = `menu-list-${entry.id.toLowerCase()}`;

  const handleClose = (event: MouseEvent | TouchEvent | React.MouseEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  };

  const handleItemClick = (path: string, isExternal?: boolean) => {
    onNavigate(path, isExternal);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <MenuListOption
        active={active}
        ref={anchorRef}
        aria-controls={open ? listId : undefined}
        aria-haspopup="true"
        onClick={() => setOpen((prevOpen) => !prevOpen)}
      >
        {entry.id === "TRANSPARENCIA" && <Info />}
        {entry.label} <ArrowDown />
      </MenuListOption>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom"
                  ? "center top"
                  : "center bottom",
            }}
          >
            <Paper style={{ padding: "6px 12px", width: "100%" }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id={listId} onKeyDown={handleListKeyDown}>
                  {(entry.children || []).map((child) =>
                    child.kind === "flyout" ? (
                      <FlyoutMenuItem
                        key={child.label}
                        label={child.label}
                        items={child.items.map((item) => ({
                          label: item.label,
                          onClick: () => handleItemClick(item.path, item.isExternal),
                        }))}
                      />
                    ) : (
                      <SubMenuItem
                        key={`${child.label}-${child.path}`}
                        onClick={() => handleItemClick(child.path, child.isExternal)}
                      >
                        {child.label}
                        {child.isExternal && <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />}
                      </SubMenuItem>
                    )
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
}

export function NavBarComponent() {
  const anchorOutrasOpcoesRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const routeParams = useParams<{ state?: string; city?: string }>();
  const location = useLocation();
  const openMenuLink = useMenuLinkNavigation();

  const buildPath = (path: string): string => {
    if (!routeParams.state || !routeParams.city) return path;
    if (path === '/') return `/${routeParams.state}/${routeParams.city}`;
    return `/${routeParams.state}/${routeParams.city}${path}`;
  };

  const [openOutrasOpcoes, setOpenOutrasOpcoes] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { applicationData } = useGlobalState();

  // Barra principal na ordem definida pela entidade (ou a padrão); "Acesso a Informações" (menus da entidade) antes do Fale Conosco
  const { menus, mainMenus } = useMenusData();
  const hasMenus = menus.length > 0;
  const navStructure = buildNavStructure(mainMenus);

  // Close MegaMenu when ESC key is pressed
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openOutrasOpcoes) {
        setOpenOutrasOpcoes(false);
      }
    };

    if (openOutrasOpcoes) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [openOutrasOpcoes]);

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
    if (searchOpen) {
      setSearchTerm('');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim().length >= 3) {
      navigate(`${buildPath('/busca')}?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchOpen(false);
      setSearchTerm('');
    }
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenOutrasOpcoes(false);
    }
  }

  const handleCloseOutrasOpcoes = (event: MouseEvent | TouchEvent | React.MouseEvent) => {
    if (
      anchorOutrasOpcoesRef.current &&
      anchorOutrasOpcoesRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenOutrasOpcoes(false);
  };

  const handleToggleOutrasOpcoes = () => {
    setOpenOutrasOpcoes((prevOpen) => !prevOpen);
  };

  const checkLocation = (route: string) => {
    const path = route.startsWith('/') ? route : `/${route}`;
    return location.pathname === buildPath(path);
  };

  const isEntryActive = (entry: NavEntry) => entryPaths(entry).some(checkLocation);

  const entityDisplay = formatEntityName(applicationData?.entity?.name);

  const megaMenuEntry = hasMenus && (
                      <div style={{ position: "relative" }} key="acesso-informacoes">
                        <MenuListOption
                          ref={anchorOutrasOpcoesRef}
                          aria-controls={openOutrasOpcoes ? "menu-list-outras-opcoes" : undefined}
                          aria-haspopup="true"
                          onClick={handleToggleOutrasOpcoes}
                        >
                          ACESSO A INFORMAÇÕES <ArrowDown />
                        </MenuListOption>
                        <Popper
                          open={openOutrasOpcoes}
                          anchorEl={anchorOutrasOpcoesRef.current}
                          role={undefined}
                          transition
                          disablePortal
                          placement="bottom"
                          modifiers={[
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 8],
                              },
                            },
                            {
                              name: 'preventOverflow',
                              options: {
                                boundary: 'viewport',
                                padding: 8,
                              },
                            },
                          ]}
                          style={{
                            zIndex: 1300,
                            width: '100vw',
                            position: 'fixed',
                            left: '0 !important',
                            right: 0,
                            transform: 'none !important',
                            maxHeight: 'calc(100vh - 200px)',
                            overflowY: 'auto',
                          }}
                        >
                          {({ TransitionProps, placement }) => (
                            <Grow
                              {...TransitionProps}
                              style={{
                                transformOrigin:
                                  placement === "bottom"
                                    ? "center top"
                                    : "center bottom",
                              }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  px: 2,
                                  py: 2,
                                  maxHeight: 'calc(100vh - 200px)',
                                  overflowY: 'auto',
                                  backgroundColor: '#f5f5f5',
                                }}
                              >
                                <ClickAwayListener onClickAway={handleCloseOutrasOpcoes}>
                                  <Box
                                    id="menu-list-outras-opcoes"
                                    onKeyDown={handleListKeyDown}
                                    sx={{
                                      width: '100%',
                                      maxWidth: '1536px',
                                    }}
                                  >
                                    <MegaMenuComponent
                                      menus={menus}
                                      onItemClick={() => setOpenOutrasOpcoes(false)}
                                    />
                                  </Box>
                                </ClickAwayListener>
                              </Box>
                            </Grow>
                          )}
                        </Popper>
                      </div>
  );

  const renderEntry = (entry: NavEntry) => {
    if (entry.children) {
      return <NavDropdown key={entry.id} entry={entry} active={isEntryActive(entry)} onNavigate={openMenuLink} />;
    }

    return (
      <MenuListOption
        key={entry.id}
        active={isEntryActive(entry)}
        onClick={() => openMenuLink(entry.path, entry.isExternal)}
      >
        {entry.label}
        {entry.isExternal && <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />}
      </MenuListOption>
    );
  };

  const faleConoscoIndex = navStructure.findIndex((entry) => entry.id === FALE_CONOSCO);
  const megaMenuIndex = faleConoscoIndex >= 0 ? faleConoscoIndex : navStructure.length;

  return (
    <>
      <AppBarWrapper>
        <AppBar position="static" color="transparent">
          <Container maxWidth="xl" disableGutters>
            <Toolbar disableGutters sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 0, padding: 0 }}>
              <Grid
                container
                spacing={2}
                justifyContent="space-between"
                alignItems="center"
                sx={{ padding: '8px 0' }}
              >
                <Grid item xs="auto">
                  <LogoEntityWrapper onClick={() => navigate(buildPath("/"))}>
                    <LogoImage
                      src={
                        applicationData?.entity?.brandLogo
                          ? `data:image/png;base64,${applicationData?.entity?.brandLogo}`
                          : applicationData?.entity?.stateBrandLogo || Logo
                      }
                      alt=""
                    />
                    {entityDisplay.name && (
                      <EntityInfoWrapper>
                        {entityDisplay.type && <EntityTypeText>{entityDisplay.type}</EntityTypeText>}
                        <EntityCityName>{entityDisplay.name}</EntityCityName>
                      </EntityInfoWrapper>
                    )}
                  </LogoEntityWrapper>
                </Grid>

                {/* Search Bar */}
                <Grid item xs="auto" sx={{ ml: 'auto', mr: 2, display: { xs: 'none', md: 'block' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Fade in={searchOpen}>
                      <Paper
                        component="form"
                        onSubmit={handleSearch}
                        sx={{
                          display: searchOpen ? 'flex' : 'none',
                          alignItems: 'center',
                          width: { sm: 350, md: 450 },
                          px: 2,
                          py: 1,
                          borderRadius: 25,
                          boxShadow: 2,
                        }}
                      >
                        <InputBase
                          placeholder="Buscar no portal..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          sx={{ flex: 1, fontSize: '1rem' }}
                          autoFocus
                        />
                      </Paper>
                    </Fade>

                    <IconButton
                      onClick={handleSearchToggle}
                      sx={{
                        color: 'white',
                        backgroundColor: 'primary.main',
                        boxShadow: 3,
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                          boxShadow: 4,
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      aria-label="buscar"
                      size="large"
                    >
                      {searchOpen ? <CloseIcon fontSize="large" /> : <SearchIcon fontSize="large" />}
                    </IconButton>
                  </Box>
                </Grid>

                <MenuButonGridItem className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-auto">
                  <MenuButton onClick={() => setIsDrawerOpen(true)}>
                    <Menu />
                  </MenuButton>
                </MenuButonGridItem>
              </Grid>
              <Grid container justifyContent="center">
                <Grid item xs={12}>
                  <MenuListWrapper>
                    {navStructure.slice(0, megaMenuIndex).map(renderEntry)}
                    {megaMenuEntry}
                    {navStructure.slice(megaMenuIndex).map(renderEntry)}
                  </MenuListWrapper>
                </Grid>
              </Grid>
            </Toolbar>
          </Container>
        </AppBar>
      </AppBarWrapper>
      <DrawerComponent
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
}
